import { Project } from "@app/project/model";
import { LOG_CATEGORY, LOG_SUB_TYPE, LOG_TYPE } from "../services/audit-log.constants";

import { DbHelper } from "@app/shared/model/db-object.model";
import { ImportMap } from "@app/shared/resources/class-importer-exporter.service";
import { AppHelperService } from "@app/shared/services/app-helper.service";

export interface AuditTypeDb {
    id: string;
    category: string;
    type: string;
    sub_type: string;
    uuid_required: boolean;
}

export interface AuditType {
    id: number;
    category: LOG_CATEGORY;
    type: LOG_TYPE;
    subType: LOG_SUB_TYPE;
    uuidRequired: boolean;
}

export class AuditTypeHelper extends DbHelper<AuditType, AuditTypeDb> {
    static readonly dbTypeName = "Audit Log Type";
    static readonly dbTypeNamePlural = "Audit Log Types";

    static readonly importMap: ImportMap<AuditType, AuditTypeDb> = [
        { i: "id", o: "id" },
        { i: "category", o: "category" },
        { i: "type", o: "type" },
        { i: "sub_type", o: "subType" },
        { i: "uuid_required", o: "uuidRequired" },
    ];

    readonly importMap = AuditTypeHelper.importMap;
}

export interface AuditLogDb {
    id: number; // auto generate from DB
    audit_type_id: number; // reference the id of audit type (see GetAllAuditTypes)

    entity_id: number;
    project_id: number;

    email: string; // this is read-only, will be ignored when create/update
    create_time: string; // this is read-only, will be ignored when create/update
    first_name: string;
    last_name: string;

    value: string; // can be any json (number, string, array, object, ... etc)
    uuid: string; // null or uuid (depend on audit type's uuid_required)

    end_time: string; // finish time
}

export interface IAuditLogLocalFilter {
    query: string;
    logParams: IAuditLogFilterParams;
}

export interface IAuditLogFilterParams {
    type?: string;
    subType?: string;
    startTime?: string;
    endTime?: string;
    uuid?: string;
    email?: string;
    projectId?: number;
    entityId?: number;
}

export interface IAuditLogFilterParamsDb {
    type?: string;
    sub_type?: string;
    start_time?: string;
    end_time?: string;
    uuid?: string;
    email?: string;
    project_id?: number;
    entity_id?: number;
}

export interface AuditLog {
    id: number;
    auditTypeId: number;
    auditType?: AuditType;
    auditTypeDisplay?: string;
    userName?: string;
    email: string;
    firstName?: string;
    lastName?: string;
    createTime: string;
    value: string;
    uuid: string;
    endTime: string;
    entityId: number;
    projectId?: number;
}

export interface AuditLogUserParams {
    userName: string; // this is the combination from first and last name, nullable
    email: string;
}

export class AuditLogHelper extends DbHelper<AuditLog, AuditLogDb> {
    static readonly dbTypeName = "Audit";
    static readonly dbTypeNamePlural = "Audits";

    static readonly importMap: ImportMap<AuditLog, AuditLogDb> = [
        { i: "id", o: "id" },
        { i: "audit_type_id", o: "auditTypeId" },
        { i: "email", o: "email" },
        { i: "first_name", o: "firstName" },
        { i: "last_name", o: "lastName" },
        { i: "create_time", o: "createTime" },
        { i: "value", o: "value" },
        { i: "uuid", o: "uuid" },
        { i: "end_time", o: "endTime" },
        { i: "entity_id", o: "entityId" },
        { i: "project_id", o: "projectId" },
    ];

    readonly importMap = AuditLogHelper.importMap;

    fromDbObject(dbObject: AuditLogDb) {
        const obj = super.fromDbObject(dbObject);

        obj.createTime = AppHelperService.getLocalDate(obj.createTime, true);
        obj.endTime = AppHelperService.getLocalDate(obj.endTime, true);
        return obj;
    }

    // Use this new method instead of the one above because entityId should be required now.
    static getAuditLogCreateData(
        auditTypeId: number,
        value: string,
        entityId: number,
        uuid?: string,
        projectId?: number,
        shouldAddEndTime = true
    ): AuditLog {
        return {
            id: null,
            auditTypeId,
            entityId,
            email: null,
            createTime: null,
            value,
            uuid,
            endTime: shouldAddEndTime ? AppHelperService.getCurrentDate() : null,
            projectId,
        };
    }

    static getAuditLogFilterParams(params: IAuditLogFilterParams = {}): IAuditLogFilterParamsDb {
        return {
            type: params.type || null,
            sub_type: params.subType || null,
            start_time: params.startTime || null,
            end_time: params.endTime || null,
            uuid: params.uuid || null,
            email: params.email || null,
            project_id: params.projectId || null,
            entity_id: params.entityId || null,
        };
    }
}

export interface AuditLogInput {
    auditName: string;
    data: Record<string, any> | string;
    projectId?: number;
    entityId?: number;
    optionalParams?: Record<string, any>;
}

export const createAuditLogInput = (
    auditName,
    data: string,
    project: Partial<Project>,
    optionalParams?: Record<string, any>
): AuditLogInput => ({
    auditName,
    data,
    optionalParams,
    projectId: project?.id,
    entityId: project?.entityId,
});
